import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import ModulePanel from '../../../ui/ModulePanel/ModulePanel'
import DataTable from '../../../ui/DataTable/DataTable'
import Spinner from '../../../ui/Spinner/Spinner'
import Button from '../../../ui/Button/Button'
import PromoTypeBadge from '../Promos/PromoTypeBadge'
import ActiveStatusBadge from '../../../ui/ActiveStatusBadge/ActiveStatusBadge'
import { formatDateAsDayMontYearHHMM } from '../../../utils/date/dateFormat'
import '../Promos/PromosTable.sass'
import styles from './AmeCampaigns.module.scss'
import {
  AME_ISSUE_KIND_BY_PHRASE,
  AME_ISSUE_KIND_LABEL,
} from '../../../api/marketing/ameCampaigns/ameCampaignConstants'

const renderTimestamp = (value) => {
  if (!value?.seconds) {
    return '—'
  }
  return formatDateAsDayMontYearHHMM(value)
}

const renderCountButton = ({ value, isLoading, onClick, label }) => {
  if (isLoading) {
    return <span className={styles.countMuted}>—</span>
  }
  if (value == null) {
    return <span className={styles.countMuted}>—</span>
  }
  return (
    <button
      type="button"
      className={styles.countButton}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onClick()
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      aria-label={label}
    >
      {value}
    </button>
  )
}

function AmeCampaignsTable({ campaigns = [], isLoading, onOpenCodes }) {
  const history = useHistory()

  const columns = useMemo(
    () => [
      {
        field: 'updatedAtSort',
        headerName: 'Оновлено sort',
        type: 'number',
      },
      {
        field: 'isActive',
        headerClassName: 'TableHeadCol',
        headerName: 'Статус',
        width: 140,
        renderCell: (params) => <ActiveStatusBadge isActive={params.row.isActive === true} />,
      },
      {
        field: 'title',
        headerClassName: 'TableHeadCol',
        headerName: 'Назва',
        minWidth: 220,
        flex: 1,
        cellClassName: 'MarketingPromos-TitleCellCol',
        renderCell: (params) => <span className="MarketingPromos-TitleCell">{params.row.title || '—'}</span>,
      },
      {
        field: 'promotionTypeLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Тип',
        width: 140,
        renderCell: (params) => (
          <PromoTypeBadge variant={params.row.promoTypeVariant} label={params.row.promotionTypeLabel} />
        ),
      },
      {
        field: 'issue',
        headerClassName: 'TableHeadCol',
        headerName: 'Тип видачі',
        width: 240,
        renderCell: (params) =>
          params.row.issue?.kind === AME_ISSUE_KIND_BY_PHRASE
            ? `За фразою: ${params.row.issue.phrase}`
            : AME_ISSUE_KIND_LABEL,
      },
      {
        field: 'benefitLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Знижка',
        width: 120,
      },
      {
        field: 'dateRangeLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Період',
        width: 220,
        renderCell: (params) => <span className="MarketingPromos-PeriodCell">{params.row.dateRangeLabel || '—'}</span>,
      },
      {
        field: 'scopeLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Область',
        width: 120,
      },
      {
        field: 'excludeLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Виключення',
        minWidth: 260,
        flex: 1.2,
        renderCell: (params) => {
          const segments = Array.isArray(params.row.excludeSegments) ? params.row.excludeSegments : []
          if (!params.row.hasExclusions || !segments.length) {
            return <span className={styles.excludeCellEmpty}>—</span>
          }
          return (
            <span className={styles.excludeCell}>
              {segments.map((segment, index) => (
                <span key={`${segment.countLabel}-${index}`} className={styles.excludeSegment}>
                  {index > 0 ? <span className={styles.excludeSep}> · </span> : null}
                  <strong className={styles.excludeCount}>{segment.countLabel}</strong>
                  {segment.list ? <span className={styles.excludeList}>{` — ${segment.list}`}</span> : null}
                </span>
              ))}
            </span>
          )
        },
      },
      {
        field: 'issuedCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Видано',
        width: 100,
        renderCell: (params) =>
          renderCountButton({
            value: params.row.issue?.kind === AME_ISSUE_KIND_BY_PHRASE ? null : params.row.issuedCount,
            isLoading,
            label: `Відкрити видані промокоди акції ${params.row.title || ''}`,
            onClick: () => onOpenCodes?.({ campaign: params.row, filter: 'all' }),
          }),
      },
      {
        field: 'redeemedCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Використано',
        width: 120,
        renderCell: (params) =>
          renderCountButton({
            value: params.row.redeemedCount,
            isLoading,
            label: `Відкрити використані промокоди акції ${params.row.title || ''}`,
            onClick: () => onOpenCodes?.({ campaign: params.row, filter: 'used' }),
          }),
      },
      {
        field: 'updated',
        headerClassName: 'TableHeadCol',
        headerName: 'Оновлено',
        width: 170,
        renderCell: (params) => renderTimestamp(params.row.updated),
      },
    ],
    [isLoading, onOpenCodes]
  )

  return (
    <ModulePanel
      className={styles.panel}
      title="Акції Ame"
      buttons={
        <Button
          theme="circle"
          fill="accent"
          icon="plus"
          tooltip="Додати акцію"
          onClick={() => history.push('/marketing/ame/new')}
        />
      }
    >
      {isLoading ? (
        <Spinner type="module" />
      ) : (
        <div className={`MarketingPromos-Table ${styles.table}`}>
          <DataTable
            initialState={{
              sorting: {
                sortModel: [{ field: 'updatedAtSort', sort: 'desc' }],
              },
              columns: {
                columnVisibilityModel: {
                  updatedAtSort: false,
                },
              },
            }}
            table={{
              rows: campaigns,
              columns,
              autoHeight: true,
              disableRowSelectionOnClick: true,
              onRowDoubleClick: (params) => history.push(`/marketing/ame/${params.row.id}`),
            }}
          />
        </div>
      )}
    </ModulePanel>
  )
}

export default AmeCampaignsTable
